import type { SourcePos } from './ast.ts';
import { RebindingError, ShadowingOpError, UnboundSymbolError } from './errors.ts';
import type { Session } from './session/Session.ts';
import type { Value } from './value.ts';

/**
 * Lexical environment — a frame of name→Value bindings with an optional
 * parent. Frames are immutable; `bind` produces a new Env that shares the
 * parent pointer with the original. The op-name set is carried unchanged
 * through every frame so `bind` can check for op-name shadowing without
 * re-receiving the registry.
 */
export type Env = {
  readonly bindings: ReadonlyMap<string, Value>;
  readonly parent: Env | null;
  readonly opNames: ReadonlySet<string>;
};

/**
 * The uniform return shape of every evaluator call: the value the node
 * produced, paired with the (possibly extended) env. Most nodes return `env`
 * unchanged; binding special forms (`dataset`) return a new env so the
 * script-level sequencer can thread the binding through subsequent siblings.
 */
export type EvalResult = {
  readonly value: Value;
  readonly env: Env;
};

/** Build a root env (no parent) from a registry's op-name set. */
export const createRootEnv = (opNames: ReadonlySet<string>): Env => ({
  bindings: new Map(),
  parent: null,
  opNames,
});

/** Lookup a name. Walks the parent chain; returns undefined if unbound. */
export const lookup = (env: Env, name: string): Value | undefined => {
  if (env.bindings.has(name)) {
    return env.bindings.get(name);
  }
  return env.parent ? lookup(env.parent, name) : undefined;
};

/** True if `name` is bound anywhere in the reachable scope chain. */
export const has = (env: Env, name: string): boolean => {
  if (env.bindings.has(name)) {
    return true;
  }
  return env.parent ? has(env.parent, name) : false;
};

/** Every name available via lookup in this env (own first, then inherited). */
export const availableNames = (env: Env): readonly string[] => {
  const local: readonly string[] = Array.from(env.bindings.keys());
  const inherited: readonly string[] = env.parent ? availableNames(env.parent) : [];
  return [...local, ...inherited];
};

/**
 * Throw if `name` cannot be bound in this env — either because it shadows
 * a registered op (`ShadowingOpError`) or is already bound in any
 * reachable scope (`RebindingError`). Used by `bind` to gate commits, and
 * by binding special forms (e.g. `dataset`) to fail fast before evaluating
 * an expensive right-hand side.
 */
export const checkBindable = (env: Env, name: string, pos: SourcePos): void => {
  if (env.opNames.has(name)) {
    throw new ShadowingOpError(name, pos);
  }
  if (has(env, name)) {
    throw new RebindingError(name, pos);
  }
};

/**
 * Bind a name in this env. Returns a new Env with the binding added.
 *
 * Throws `SExprError` via {@link checkBindable} if `name` is a registered
 * op (`ShadowingOp`) or already bound in any reachable scope (`Rebinding`).
 * Callers that need to validate the name before evaluating the right-hand
 * side should use {@link checkBindable} directly.
 */
export const bind = (env: Env, name: string, value: Value, pos: SourcePos): Env => {
  checkBindable(env, name, pos);
  const bindings = new Map(env.bindings);
  bindings.set(name, value);
  return { ...env, bindings };
};
/**
 * Resolve a symbol with Env → Session fallthrough (Phase 4-prep
 * 4-prep.7 / PHASE_4_PREP.md D8). Env shadows Session: lexical
 * bindings (`(dataset foo ...)`) hide any session `foo` for the
 * duration of the surrounding expression. On both miss, throws
 * `UnboundSymbolError` whose `available` list is the deduplicated
 * union of `availableNames(env)` and `session.bindingNames()` — so
 * "did you mean ..." suggestions cover cross-turn bindings too.
 *
 * SSOT for the two call sites that need this resolution:
 *   - `evalNode`'s `symbolRef` case in `evaluator.ts` (every symbol
 *     reference in an expression).
 *   - `(col ...)`'s Form 1 binding-name resolution in `builtins.ts`
 *     (the named-binding form captures the symbol as a string for
 *     deferred resolution but still validates the binding exists
 *     and points at a Dataset at apply time).
 */
export const resolveSymbolWithSessionFallthrough = (
  env: Env,
  session: Session,
  name: string,
  pos: SourcePos,
): Value => {
  const envValue = lookup(env, name);
  if (envValue !== undefined) {
    return envValue;
  }
  const sessionValue = session.getBinding(name);
  if (sessionValue !== undefined) {
    return sessionValue;
  }
  const available = Array.from(new Set([...availableNames(env), ...session.bindingNames()]));
  throw new UnboundSymbolError(name, available, pos);
};
